import React, {
	ChangeEvent,
	FormEvent,
	useEffect,
	useReducer,
	useState,
} from 'react';
import {
	BearingCalculationData,
	BearingClass,
	BearingTypeCode,
	FittingPracticeCalculationPayload,
	FittingPracticeNavigationItem,
	InstallationCalculationType,
	TimkenUnit,
} from '../../../components/bearing-search/types/types';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';
import { text } from '../../data';
import fittingFormReducer from './reducer';
import { getFittingPracticeNavigationOptions } from './navigation';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import {
	NAVIGATION_ROOT,
	bearingTypes,
	getFittingPracticeCalculation,
} from '../../../components/bearing-search/api/api';
import CalculationResults from '../../components/CalculationResults';
import { FittingCalculationResults } from '../../types/types';
import { trackResults } from '../../utils/analytics';

export type FittingPracticeNavigationMap = Map<
	string,
	FittingPracticeNavigationItem[]
>;

interface FittingFormProps {
	unit: TimkenUnit;
	type: InstallationCalculationType;
	bearing: BearingPartState;
	bearingClass: BearingClass;
	navigation: FittingPracticeNavigationMap;
	onCalculate: ( type: InstallationCalculationType, status: boolean ) => void;
	onComplete: ( results: Partial<FittingCalculationResults> ) => void;
}

const FittingForm: React.FC<FittingFormProps> = ( {
	unit,
	type,
	bearing,
	bearingClass,
	navigation,
	onCalculate,
	onComplete,
} ) => {
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ stamp, setStamp ] = useState( '' );
	const [ thumbnail, setThumbnail ] = useState<
		{ url: string; alt: string; } | undefined
	>();
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);

	const initialFittingFormState = {
		applicationType: '',
		navigationMap: new Map( navigation ),
		selections: [
			{
				list: navigation.get( NAVIGATION_ROOT ) || [],
			},
		],
		isComplete: false,
	};

	const [ formState, dispatch ] = useReducer(
		fittingFormReducer,
		initialFittingFormState,
	);

	useEffect( () => {
		onCalculate( type, isCalculated && ! isResultsLoading );
	}, [ isCalculated, isResultsLoading ] );

	useEffect( () => {
		// Get fitting practice image
		const selectedType = bearingTypes.find(
			( bearingType ) => {
				if ( bearingType.bearingType === BearingTypeCode.TRB ) {
					return bearingType.bearingSubType === bearing.bearingSubType;
				}

				return bearingType.bearingType === bearing.bearingType;
			},
		);

		const thumbnailUrl =
			type === 'Inner'
				? selectedType?.fittingPractice?.innerImageUrl
				: selectedType?.fittingPractice?.outerImageUrl;
		const thumbnailAlt =
			type === 'Inner'
				? `${ text.innerFittingThumb } ${ bearing.bearingType }`
				: `${ text.outerFittingThumb } ${ bearing.bearingType }`;

		if ( thumbnailUrl ) {
			setThumbnail( {
				url: thumbnailUrl,
				alt: thumbnailAlt,
			} );
		} else {
			setThumbnail( undefined );
		}
	}, [ bearing ] );

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		const { partNumber, cupPartNumber, bearingType, bearingSubType } = bearing;

		const payload: Partial<FittingPracticeCalculationPayload> = {
			bearingType,
			bearingSubType,
			unit,
			partNumber,
			cupPartNumber,
			bearingClass: bearingClass.value,
			designUnit: bearingClass.designUnit,
			categoryType: type,
			applicationType: formState.selections[ 0 ]?.selected?.applicationType,
			applicationTypeData:
				formState.selections[ 0 ]?.selected?.categoryDescription,
		};

		const selections = formState.selections.map(
			( selection ) => selection.selected,
		);

		const selectionDetails: Record<string, string> = {};

		selections.forEach( ( selection ) => {
			const key = selection?.categoryLabel;
			const tableLookupValue = selection?.tableLookupValue?.trim();
			const isoIdentifierKey = selection?.isoIdentifierKey?.trim();

			if ( key ) {
				selectionDetails[ key ] = selection?.categoryDescription;
			}

			if ( tableLookupValue ) {
				payload.tableLookupId = tableLookupValue;
			}

			if ( isoIdentifierKey ) {
				payload.isoCategoryKeyId = isoIdentifierKey;
			}
		} );

		if ( type === 'Inner' ) {
			payload.componentType = 'IR';
			payload.innerSelectionDetails = [ selectionDetails ];
		} else {
			payload.componentType = 'OR';
			payload.outerSelectionDetails = [ selectionDetails ];
		}

		setIsCalculated( true );
		setIsResultsLoading( true );

		getFittingPracticeCalculation( payload )
			.then( ( response ) => {
				let reportData: Array<BearingCalculationData | null> = [];

				if ( response.report && response.report.length > 0 ) {
					reportData = response.report[ 0 ].data;
				}

				setResults( reportData );
				trackResults( 'bearingFittingPractice', payload, reportData.length, bearing.marketPlaceDescription );

				let results = document.querySelectorAll('.t-engineering-tools__results');

				if ( type === 'Inner' ) {
					onComplete( {
						minimumInnerFit: response.resultantFitMinimum,
						maximumInnerFit: response.resultantFitMaximum,
					} );

					results[0].scrollIntoView({
						behavior: 'smooth'
					});

				} else {
					onComplete( {
						minimumOuterFit: response.resultantFitMinimum,
						maximumOuterFit: response.resultantFitMaximum,
					} );

					if (results.length == 2) {
						results[1].scrollIntoView({
							behavior: 'smooth'
						});
					} else {
						results[0].scrollIntoView({
							behavior: 'smooth'
						});
					}
				}
			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsCalculated( false );

		// Update random generated stamp to force re-render of select form (to un-selected state)
		setStamp( new Date().getTime().toString() );

		dispatch( {
			type: 'reset',
			payload: initialFittingFormState,
		} );
	};

	return (
		<div>
			<form className="t-engineering-tools__sub-form" onSubmit={handleSubmit}>
				<div className="t-engineering-tools__constraints">
					<h3>
						{type === 'Inner'
							? text.innerFittingPractice
							: text.outerFittingPractice}
					</h3>

					<div className="row form-row">
						{formState.selections.map( ( dropdown, index ) => {
							const { list, selected } = dropdown;
							const { id, label, options } =
								getFittingPracticeNavigationOptions( list );

							return (
								<div key={`${ id }_${ stamp }`} className="col-md-3">
									<SelectDropdown
										id={`fitting_form_${ id }`}
										required={true}
										placeholder={! selected ? text.select : undefined}
										label={label}
										value={selected?.categoryDescription}
										options={options}
										onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
											dispatch( {
												type: 'change_option',
												payload: [
													index,
													list.find(
														( item ) =>
															item.categoryDescription === event.target.value,
													),
												],
											} )
										}
									/>
								</div>
							);
						} )}
					</div>
				</div>

				<div>
					<div  className="t-engineering-tools__terms-of-use"
						  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
					/>
					<div className="t-engineering-tools__search-actions">
						<button
							className="btn btn--primary"
							type="submit"
							disabled={! formState.isComplete}
						>
							{text.calculate}
						</button>

						<button
							className="btn btn--secondary"
							type="button"
							onClick={() => clearSearch()}
						>
							{text.clear}
						</button>
					</div>
				</div>
			</form>

			{isCalculated && (
				<CalculationResults
					data={results}
					loading={isResultsLoading}
					image={thumbnail}
				/>
			)}
		</div>
	);
};

export default FittingForm;
